import {
  format,
  differenceInYears,
  differenceInHours,
  differenceInMonths,
  formatDistanceToNow,
  parseISO,
  parse,
  addDays,
} from 'date-fns';
import type { Locale } from 'date-fns';
import it from 'date-fns/locale/it';
import enGB from 'date-fns/locale/en-GB';

export const dateFormat = `dd MMM yyyy`;

/**
 * A date value that may not be one.
 *
 * `new Date(null)` is not an error in JavaScript: it is the Unix epoch, so a missing
 * date formats as a confident "01/01/1970" and travels wherever that string goes. A
 * blank string or an unparseable one is the other half of the problem: `new Date('')`
 * is an Invalid Date, and date-fns `format` throws a RangeError on it, taking the
 * component down.
 *
 * Both are the same mistake, treating "no date" as a date, and both are silent at the
 * call site. Every caller today guards with a ternary before calling these formatters,
 * so neither bites; that is a convention, not a guarantee, and it holds only until
 * someone formats an optional field without remembering.
 *
 * Returns `null` for anything that is not a real date, so the formatters can answer with
 * an empty string: nothing shown is right for a date nobody entered, where both 1970 and
 * a crash are wrong.
 */
const parseDateOrNull = (date?: Date | string | null): Date | null => {
  if (date === null || date === undefined || date === '') return null;

  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatDate = (date?: Date | string | null): string => {
  const parsed = parseDateOrNull(date);
  return parsed ? format(parsed, dateFormat, { locale: it }) : '';
};

/**
 * date-fns locale for a next-i18next locale code.
 *
 * Italian is the fallback because it is the app's `defaultLocale`: an unknown
 * code must not silently drop to date-fns' built-in en-US, which is how the
 * application timeline ended up printing one Italian and one English date in
 * the same sentence.
 */
export const getDateFnsLocale = (locale?: string): Locale => (locale?.startsWith('en') ? enGB : it);

/** `formatDate` in the reader's language. `formatDate` itself stays Italian-only: it has callers well outside any localised screen. */
export const formatDateIn = (date?: Date | string | null, locale?: string, dateFormatString?: string): string => {
  const parsed = parseDateOrNull(date);
  if (!parsed) return '';

  return format(parsed, dateFormatString || dateFormat, { locale: getDateFnsLocale(locale) });
};

export const toDateFormat = (date?: Date | string | null, newDateFormat?: string): string => {
  const parsed = parseDateOrNull(date);
  return parsed ? format(parsed, newDateFormat || dateFormat, { locale: it }) : '';
};
export const genericDateFormat = 'yyyy-MM-dd';

export const getYearsElapsed = (date: Date | string) => {
  const today = new Date();
  const birthDate = new Date(date);
  return differenceInYears(today, birthDate);
};

export const getMonthsElapsed = (date: Date | string) => {
  const today = new Date();
  const comparison = new Date(date);
  return differenceInMonths(today, comparison);
};

export const getHoursElapsed = (date: Date | string) => {
  const today = new Date();
  const comparison = new Date(date);
  return differenceInHours(today, comparison);
};

export const getTimeElapsed = (date: Date | string) => {
  return formatDistanceToNow(new Date(date), { locale: it });
};
export const formatDateTo = (date: Date | string, dateFormatString: string): string =>
  format(new Date(date), dateFormatString, { locale: it });

/**
 * Formats an ISO date string into 'dd/MM/yy' format.
 *
 * @param {string} [dateStr] - The ISO date string to format.
 * @returns {string} - The formatted date, or an empty string if input is invalid.
 */
export function formatDateToDayMonthYear(dateStr?: string): string {
  if (!dateStr) return '';
  const date = parseISO(dateStr);
  return format(date, 'dd/MM/yy');
}

export function addDaysToDayMonthYear(dateStr?: string, daysToAdd = 1) {
  if (!dateStr) return '';
  const parsedDate = parse(dateStr, 'dd/MM/yy', new Date());
  const dateWithAddition = addDays(parsedDate, daysToAdd);
  return format(dateWithAddition, 'dd/MM/yy');
}
